/** Translation dictionary, keyed by screen, English + Bahasa Indonesia.
 *  Shared keys (sidebar, profileMenu, settings, login, …) mirror
 *  mobile/lib/i18n.js so both clients read the same. */
export const translations = {
  en: {
    common: {
      cancel: 'Cancel',
      delete: 'Delete',
      done: 'Done',
      close: 'Close',
      back: 'Back',
      edit: 'Edit',
      none: 'None',
      share: 'Share',
      linkCopied: 'Link copied!',
      sources: 'Sources',
      category: 'Category',
      project: 'Project',
      noCategory: 'No category',
      noProject: 'No project',
      untitled: 'Untitled',
      note: 'Note',
    },
    time: {
      justNow: 'just now',
      minutesAgo: '{n}m ago',
      hoursAgo: '{n}h ago',
      daysAgo: '{n}d ago',
    },
    source: {
      youtube: 'YouTube',
      tiktok: 'TikTok',
      instagram: 'Instagram',
      link: 'Link',
      pdf: 'PDF',
      image: 'Image',
      note: 'Note',
    },
    topBar: {
      searchPlaceholder: 'Search your brain…',
      menu: 'Menu',
      search: 'Search',
      clear: 'Clear',
      refresh: 'Refresh',
    },
    bottomBar: {
      searchPlaceholder: 'Search',
      newNote: 'New note',
    },
    home: {
      selectedCount: '{n} selected',
      cancelSelection: 'Cancel selection',
      shareSelected: 'Share selected',
      deleteSelected: 'Delete selected',
      bulkDeleteConfirm: 'Delete {n} note{s}? This removes them from your brain.',
      deleteConfirm: 'Delete "{title}"? This removes it from your brain.',
      deleteFailed: 'Delete failed — {error}',
      loading: 'Loading…',
      noMatches: 'No matches',
      noMatchesHint: 'Try a different phrasing — the AI looks at meaning, not just words.',
      emptyFilter: 'No notes here yet',
      emptyFilterHint: 'Add one from the Take a note box above.',
      emptyTitle: 'Notes you add appear here',
      emptyHint: 'Drop a quick thought, paste a link, or attach a PDF — your brain remembers.',
      loadingMore: 'Loading more…',
      dropTitle: 'Drop to save as a note',
      dropSubtitle: 'Images and PDFs',
    },
    sidebar: {
      allNotes: 'All notes',
      text: 'Text',
      links: 'Links',
      youtube: 'YouTube',
      pdfs: 'PDFs',
      images: 'Images',
      categories: 'Categories',
      projects: 'Projects',
      searchCategories: 'Search categories…',
      searchProjects: 'Search projects…',
      closeSearch: 'Close search',
      noMatches: 'No matches',
      categoryName: 'Category name',
      projectName: 'Project name',
      createCategory: 'Create category',
      createProject: 'Create project',
      createCategoryFailed: 'Could not create category',
      createProjectFailed: 'Could not create project',
      deleteCategoryConfirm: 'Delete category "{name}"? Notes will be uncategorised.',
      deleteProjectConfirm: 'Delete project "{name}"? Notes will be unassigned.',
    },
    profileMenu: {
      account: 'Account',
      settings: 'Settings',
      signOut: 'Sign out',
      signIn: 'Sign in',
      stubHint: 'Cognito not configured — running as dev-user',
    },
    settings: {
      title: 'Settings',
      language: 'Language',
      english: 'English',
      indonesian: 'Bahasa Indonesia',
      theme: 'Theme',
      light: 'Light',
      dark: 'Dark',
      mcpTitle: 'MCP Server',
      mcpDesc: 'Paste this URL into Claude.ai or ChatGPT as a custom connector to let it browse your notes.',
      copy: 'Copy',
      copied: 'Copied',
    },
    login: {
      subtitle: 'Your private, semantic notebook. Sign in with Google to access your brain.',
      consent:
        'I agree to the storage of my notes and embeddings in my own data partition, and to the use of my Google profile (email, name, picture) for authentication. I can delete my data anytime from the app.',
      continueWithGoogle: 'Continue with Google',
      footer: 'AWS Cognito + Google OAuth. Per-account data partitioning.',
    },
    authCallback: {
      failed: 'Sign-in failed',
      backHome: 'Back home',
      finishing: 'Finishing sign-in…',
    },
    noteCard: {
      processing: 'Processing',
      processingHint: 'Saving, summarising and indexing… this can take a few seconds.',
      failed: 'Failed',
      couldNotSave: "Couldn't save this note.",
      dismiss: 'Dismiss',
      noReason: 'No reason yet — click to add one.',
      select: 'Select note',
      deselect: 'Deselect note',
    },
    noteDetail: {
      openOnYoutube: 'Open on YouTube',
      openSource: 'Open source',
      edited: '· edited {time}',
      saving: 'Saving…',
      titlePlaceholder: 'Title',
      bodyPlaceholder: 'Edit your note…',
      reasonPdf: 'Why did you save this PDF — what to remember, key takeaways…',
      reasonImage: 'Caption — what is this a reference for?',
      reasonVideo: 'Why did you save this video — what to remember…',
      reasonLink: 'Why did you save this link — what to remember…',
      contentEmpty: 'Content cannot be empty.',
      captionEmpty: 'Caption cannot be empty for an image note.',
      updateFailed: 'Update failed',
      viewPdf: 'View PDF',
      viewImage: 'View image',
    },
    viewer: {
      openInNewTab: 'Open in new tab',
      imageFailed: 'Failed to load image',
      pdfFailed: 'Failed to load PDF',
    },
    takeNote: {
      takeANote: 'Take a note…',
      titlePlaceholder: 'Title',
      bodyPlaceholderImage: 'Add a caption — what is this a reference for? (required)',
      bodyPlaceholderLink: 'Add a reason for this link… (optional)',
      bodyPlaceholderFile: 'Add a reason for this file… (optional)',
      bodyPlaceholderDefault:
        'Paste a link, then keep typing your reason… or just write a note. Or paste/drop an image.',
      detectedYoutube: 'Detected YouTube — rest of the text becomes your reason',
      detectedLink: 'Detected link — rest of the text becomes your reason',
      multiLinkDetected: '{n} links detected — saved as a note, listed as sources',
      newCategory: 'New category',
      newProject: 'New project',
      newCategoryPrompt: 'New category name',
      newProjectPrompt: 'New project name',
      attachPdf: 'Attach PDF',
      attachImage: 'Attach image',
      attachAudio: 'Attach audio',
      removeAttachment: 'Remove attachment',
      undo: 'Undo',
      redo: 'Redo',
    },
    publicView: {
      sharedNote: 'Shared note · read only',
      sharedNotes: '{n} shared note{s} · read only',
      noteUnavailable: 'This note is unavailable — it may have been deleted.',
      notesUnavailable: 'These notes are unavailable — they may have been deleted.',
      noteLoadFailed: 'Failed to load note',
      notesLoadFailed: 'Failed to load notes',
      goToApp: 'Go to Recycle Brain',
      ctaTitle: 'Save and organise your own notes with Recycle Brain',
      ctaBody: 'Drop links, videos, PDFs and quick thoughts — search them later with AI.',
      ctaButton: 'Try Recycle Brain',
    },
    share: {
      noteTitle: 'Recycle Brain note',
      noteText: 'Check out this note: {title}',
      noteTextUntitled: 'Check out this note on Recycle Brain',
      collectionTitle: 'Recycle Brain notes',
      collectionText: 'Check out these {n} notes on Recycle Brain',
    },
  },
  id: {
    common: {
      cancel: 'Batal',
      delete: 'Hapus',
      done: 'Selesai',
      close: 'Tutup',
      back: 'Kembali',
      edit: 'Ubah',
      none: 'Tidak ada',
      share: 'Bagikan',
      linkCopied: 'Tautan tersalin!',
      sources: 'Sumber',
      category: 'Kategori',
      project: 'Proyek',
      noCategory: 'Tanpa kategori',
      noProject: 'Tanpa proyek',
      untitled: 'Tanpa judul',
      note: 'Catatan',
    },
    time: {
      justNow: 'baru saja',
      minutesAgo: '{n} mnt lalu',
      hoursAgo: '{n} jam lalu',
      daysAgo: '{n} hr lalu',
    },
    source: {
      youtube: 'YouTube',
      tiktok: 'TikTok',
      instagram: 'Instagram',
      link: 'Tautan',
      pdf: 'PDF',
      image: 'Gambar',
      note: 'Catatan',
    },
    topBar: {
      searchPlaceholder: 'Cari di otakmu…',
      menu: 'Menu',
      search: 'Cari',
      clear: 'Hapus pencarian',
      refresh: 'Muat ulang',
    },
    bottomBar: {
      searchPlaceholder: 'Cari',
      newNote: 'Catatan baru',
    },
    home: {
      selectedCount: '{n} dipilih',
      cancelSelection: 'Batalkan pilihan',
      shareSelected: 'Bagikan yang dipilih',
      deleteSelected: 'Hapus yang dipilih',
      bulkDeleteConfirm: 'Hapus {n} catatan? Ini akan menghapusnya dari otakmu.',
      deleteConfirm: 'Hapus "{title}"? Ini akan menghapusnya dari otakmu.',
      deleteFailed: 'Gagal menghapus — {error}',
      loading: 'Memuat…',
      noMatches: 'Tidak ada hasil',
      noMatchesHint: 'Coba kata lain — AI memahami makna, bukan hanya kata.',
      emptyFilter: 'Belum ada catatan di sini',
      emptyFilterHint: 'Tambahkan dari kotak Tulis catatan di atas.',
      emptyTitle: 'Catatan yang kamu tambahkan muncul di sini',
      emptyHint: 'Tulis ide singkat, tempel tautan, atau lampirkan PDF — otakmu akan mengingatnya.',
      loadingMore: 'Memuat lagi…',
      dropTitle: 'Lepaskan untuk menyimpan sebagai catatan',
      dropSubtitle: 'Gambar dan PDF',
    },
    sidebar: {
      allNotes: 'Semua catatan',
      text: 'Teks',
      links: 'Tautan',
      youtube: 'YouTube',
      pdfs: 'PDF',
      images: 'Gambar',
      categories: 'Kategori',
      projects: 'Proyek',
      searchCategories: 'Cari kategori…',
      searchProjects: 'Cari proyek…',
      closeSearch: 'Tutup pencarian',
      noMatches: 'Tidak ada hasil',
      categoryName: 'Nama kategori',
      projectName: 'Nama proyek',
      createCategory: 'Buat kategori',
      createProject: 'Buat proyek',
      createCategoryFailed: 'Gagal membuat kategori',
      createProjectFailed: 'Gagal membuat proyek',
      deleteCategoryConfirm: 'Hapus kategori "{name}"? Catatan di dalamnya jadi tanpa kategori.',
      deleteProjectConfirm: 'Hapus proyek "{name}"? Catatan di dalamnya jadi tanpa proyek.',
    },
    profileMenu: {
      account: 'Akun',
      settings: 'Pengaturan',
      signOut: 'Keluar',
      signIn: 'Masuk',
      stubHint: 'Cognito belum dikonfigurasi — berjalan sebagai dev-user',
    },
    settings: {
      title: 'Pengaturan',
      language: 'Bahasa',
      english: 'English',
      indonesian: 'Bahasa Indonesia',
      theme: 'Tema',
      light: 'Terang',
      dark: 'Gelap',
      mcpTitle: 'Server MCP',
      mcpDesc: 'Tempel URL ini ke Claude.ai atau ChatGPT sebagai konektor kustom agar bisa membaca catatanmu.',
      copy: 'Salin',
      copied: 'Tersalin',
    },
    login: {
      subtitle: 'Buku catatan semantik pribadimu. Masuk dengan Google untuk mengakses otakmu.',
      consent:
        'Saya setuju catatan dan embedding saya disimpan di partisi data saya sendiri, dan profil Google saya (email, nama, foto) digunakan untuk autentikasi. Saya bisa menghapus data saya kapan saja dari aplikasi.',
      continueWithGoogle: 'Lanjutkan dengan Google',
      footer: 'AWS Cognito + Google OAuth. Partisi data per akun.',
    },
    authCallback: {
      failed: 'Gagal masuk',
      backHome: 'Kembali ke beranda',
      finishing: 'Menyelesaikan proses masuk…',
    },
    noteCard: {
      processing: 'Memproses',
      processingHint: 'Menyimpan, meringkas, dan mengindeks… ini bisa memakan beberapa detik.',
      failed: 'Gagal',
      couldNotSave: 'Catatan ini gagal disimpan.',
      dismiss: 'Tutup',
      noReason: 'Belum ada alasan — klik untuk menambah.',
      select: 'Pilih catatan',
      deselect: 'Batal pilih catatan',
    },
    noteDetail: {
      openOnYoutube: 'Buka di YouTube',
      openSource: 'Buka sumber',
      edited: '· diubah {time}',
      saving: 'Menyimpan…',
      titlePlaceholder: 'Judul',
      bodyPlaceholder: 'Edit catatanmu…',
      reasonPdf: 'Kenapa kamu menyimpan PDF ini — apa yang perlu diingat, poin penting…',
      reasonImage: 'Keterangan — ini referensi untuk apa?',
      reasonVideo: 'Kenapa kamu menyimpan video ini — apa yang perlu diingat…',
      reasonLink: 'Kenapa kamu menyimpan tautan ini — apa yang perlu diingat…',
      contentEmpty: 'Konten tidak boleh kosong.',
      captionEmpty: 'Keterangan wajib diisi untuk catatan gambar.',
      updateFailed: 'Gagal memperbarui',
      viewPdf: 'Lihat PDF',
      viewImage: 'Lihat gambar',
    },
    viewer: {
      openInNewTab: 'Buka di tab baru',
      imageFailed: 'Gagal memuat gambar',
      pdfFailed: 'Gagal memuat PDF',
    },
    takeNote: {
      takeANote: 'Tulis catatan…',
      titlePlaceholder: 'Judul',
      bodyPlaceholderImage: 'Tambahkan keterangan — ini referensi untuk apa? (wajib)',
      bodyPlaceholderLink: 'Tambahkan alasan untuk tautan ini… (opsional)',
      bodyPlaceholderFile: 'Tambahkan alasan untuk file ini… (opsional)',
      bodyPlaceholderDefault:
        'Tempel tautan, lalu lanjutkan menulis alasanmu… atau tulis catatan biasa. Bisa juga tempel/lepas gambar.',
      detectedYoutube: 'Terdeteksi YouTube — sisa teks jadi alasanmu',
      detectedLink: 'Terdeteksi tautan — sisa teks jadi alasanmu',
      multiLinkDetected: '{n} tautan terdeteksi — disimpan sebagai catatan, tercantum sebagai sumber',
      newCategory: 'Kategori baru',
      newProject: 'Proyek baru',
      newCategoryPrompt: 'Nama kategori baru',
      newProjectPrompt: 'Nama proyek baru',
      attachPdf: 'Lampirkan PDF',
      attachImage: 'Lampirkan gambar',
      attachAudio: 'Lampirkan audio',
      removeAttachment: 'Hapus lampiran',
      undo: 'Urungkan',
      redo: 'Ulangi',
    },
    publicView: {
      sharedNote: 'Catatan dibagikan · hanya baca',
      sharedNotes: '{n} catatan dibagikan · hanya baca',
      noteUnavailable: 'Catatan ini tidak tersedia — mungkin sudah dihapus.',
      notesUnavailable: 'Catatan-catatan ini tidak tersedia — mungkin sudah dihapus.',
      noteLoadFailed: 'Gagal memuat catatan',
      notesLoadFailed: 'Gagal memuat catatan',
      goToApp: 'Buka Recycle Brain',
      ctaTitle: 'Simpan dan atur catatanmu sendiri dengan Recycle Brain',
      ctaBody: 'Simpan tautan, video, PDF, dan ide singkat — cari lagi nanti dengan AI.',
      ctaButton: 'Coba Recycle Brain',
    },
    share: {
      noteTitle: 'Catatan Recycle Brain',
      noteText: 'Lihat catatan ini: {title}',
      noteTextUntitled: 'Lihat catatan ini di Recycle Brain',
      collectionTitle: 'Catatan Recycle Brain',
      collectionText: 'Lihat {n} catatan ini di Recycle Brain',
    },
  },
};

export function translate(lang, path, vars) {
  const dict = translations[lang] || translations.en;
  const parts = path.split('.');
  let node = dict;
  for (const p of parts) node = node?.[p];
  if (node === undefined) {
    // Fall back to English so a missing ID key never renders blank.
    node = parts.reduce((n, p) => n?.[p], translations.en);
  }
  if (typeof node !== 'string') return path;
  if (!vars) return node;
  return Object.keys(vars).reduce(
    (str, key) => str.replace(new RegExp(`\\{${key}\\}`, 'g'), String(vars[key])),
    node,
  );
}
